import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import StopSaleModal from '../../components/modals/StopSaleModal';
import { salesApiService } from '../../api/salesApi';
import reportManager from '../../reports/ReportTemplateManager';
import { PlusIcon, PrinterIcon } from '@heroicons/react/24/outline';
import DataTable from '../../components/ui/DataTable';
import FilterPanel from '../../components/ui/FilterPanel';
import FilterActions from '../../components/ui/FilterActions';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import { createColumnHelper } from '@tanstack/react-table';

const defaultFilters = (hotels, maleOnly) => {
  const normalOption = maleOnly.find(
    (male) =>
      male.is_maleonly === 0 ||
      male.is_maleonly === '0' ||
      male.is_maleonly?.toString().toLowerCase().includes('normal')
  );
  return {
    hotel: hotels.length > 0 ? hotels[0].id : '',
    country: '',
    dateType: '',
    startDate: '',
    finishDate: '',
    market: '',
    operators: '',
    type: '',
    rateOnly: normalOption ? normalOption.id : '',
    active: 'ALL',
    room: '',
    confirmStatus: '',
    userType: 'ALL',
  };
};

const StopSaleDashboard = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stopSaleEntries, setStopSaleEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [dateTypes, setDateTypes] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [openStopTypes, setOpenStopTypes] = useState([]);
  const [maleOnly, setMaleOnly] = useState([]);
  const [filters, setFilters] = useState(() => defaultFilters([], []));
  const [filterPanelOpen, setFilterPanelOpen] = useState(true);

  useEffect(() => {
    loadInitialData();
    loadStopSaleEntries();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [hotelsRes, roomTypesRes, dateTypesRes, marketsRes, operatorsRes, openStopTypesRes, maleOnlyRes] =
        await Promise.all([
          salesApiService.getHotels(),
          salesApiService.getRoomTypes(),
          salesApiService.getDateTypes(),
          salesApiService.getMarkets(),
          salesApiService.getOperators(),
          salesApiService.getOpenStopTypes(),
          salesApiService.getMaleOnly(),
        ]);

      const hotelsData = Array.isArray(hotelsRes) ? hotelsRes : hotelsRes.data || [];
      const roomTypesData = Array.isArray(roomTypesRes) ? roomTypesRes : roomTypesRes.data || [];
      const dateTypesData = Array.isArray(dateTypesRes) ? dateTypesRes : dateTypesRes.data || [];
      const marketsData = Array.isArray(marketsRes) ? marketsRes : marketsRes.data || [];
      const openStopTypesData = Array.isArray(openStopTypesRes) ? openStopTypesRes : openStopTypesRes.data || [];
      const maleOnlyData = Array.isArray(maleOnlyRes) ? maleOnlyRes : maleOnlyRes.data || [];

      setHotels(hotelsData);
      setRoomTypes(roomTypesData);
      setDateTypes(dateTypesData);
      setMarkets(marketsData);
      setOpenStopTypes(openStopTypesData);
      setMaleOnly(maleOnlyData);

      setFilters(defaultFilters(hotelsData, maleOnlyData));
    } catch (err) {
      setError('Veriler yüklenirken hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStopSaleEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await salesApiService.getOpenStopSales();

      let entries = [];
      if (Array.isArray(response)) {
        entries = response;
      } else if (response.data && Array.isArray(response.data)) {
        entries = response.data;
      } else if (response.success && response.data && Array.isArray(response.data)) {
        entries = response.data;
      }

      setStopSaleEntries(entries);
    } catch (err) {
      setError('Stop sale kayıtları yüklenirken hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'hotel') {
        next.room = '';
      }
      return next;
    });
  };

  const getFilteredEntries = () => {
    const filtered = stopSaleEntries.filter((entry) => {
      if (filters.hotel && filters.hotel !== '') {
        const selectedHotel = hotels.find((h) => h.id === parseInt(filters.hotel, 10));
        if (selectedHotel && entry.hotel_name !== selectedHotel.hotel_name) {
          return false;
        }
      }

      if (filters.dateType && filters.dateType !== '') {
        const selectedDateType = dateTypes.find((dt) => dt.id === parseInt(filters.dateType, 10));
        if (selectedDateType) {
          const entryDtId =
            entry.date_type && typeof entry.date_type === 'object' && entry.date_type.id != null
              ? entry.date_type.id
              : entry.date_type;
          const idMatch = entryDtId != null && Number(entryDtId) === Number(selectedDateType.id);
          const nameMatch =
            (entry.date_type_name || '').trim() === (selectedDateType.date_type || '').trim();
          if (!idMatch && !nameMatch) {
            return false;
          }
        }
      }

      if (filters.market && filters.market !== '' && filters.market !== 'ALL') {
        const selectedMarket = markets.find((m) => m.id === parseInt(filters.market, 10));
        if (selectedMarket && entry.market_name !== selectedMarket.market) {
          return false;
        }
      }

      if (filters.room && filters.room !== '' && filters.room !== 'ALL') {
        const selectedRoomType = roomTypes.find((rt) => rt.id === parseInt(filters.room, 10));
        if (selectedRoomType && entry.roomtype_name !== selectedRoomType.roomtype_name) {
          return false;
        }
      }

      if (filters.type && filters.type !== '' && filters.type !== 'ALL') {
        const openStopId =
          entry.openstoptype && typeof entry.openstoptype === 'object' && entry.openstoptype.id != null
            ? entry.openstoptype.id
            : entry.openstoptype;
        if (Number(openStopId) !== Number(parseInt(filters.type, 10))) {
          return false;
        }
      }

      if (filters.rateOnly && filters.rateOnly !== '') {
        const selectedRateOnly = maleOnly.find((mo) => mo.id === parseInt(filters.rateOnly, 10));
        if (selectedRateOnly) {
          const entryMaleId =
            entry.maleonly && typeof entry.maleonly === 'object' && entry.maleonly.id != null
              ? entry.maleonly.id
              : entry.maleonly;
          if (Number(entryMaleId) !== Number(selectedRateOnly.id)) {
            return false;
          }
        }
      }

      if (filters.startDate && entry.start_date < filters.startDate) {
        return false;
      }

      if (filters.finishDate && entry.finish_date > filters.finishDate) {
        return false;
      }

      return true;
    });

    return filtered;
  };

  const handleSaveStopSale = async (data) => {
    try {
      setLoading(true);

      if (Array.isArray(data)) {
        const response = await salesApiService.createOpenStopSalesBulk(data);
        const items = response.items || [];
        if (items.length) {
          setStopSaleEntries((prev) => [...prev, ...items]);
        }
        setIsModalOpen(false);
        await loadStopSaleEntries();
        return;
      }

      let response;
      if (data.id) {
        response = await salesApiService.updateOpenStopSale(data.id, data);
        const updatedEntry = response.data || response;

        setStopSaleEntries((prev) => prev.map((item) => (item.id === data.id ? updatedEntry : item)));
      } else {
        response = await salesApiService.createOpenStopSale(data);
        const newEntry = response.data || response;
        setStopSaleEntries((prev) => [...prev, newEntry]);
      }

      setIsModalOpen(false);
      await loadStopSaleEntries();
    } catch (err) {
      let errorMessage = 'İşlem sırasında hata oluştu: ';

      if (err.response?.data?.message) {
        errorMessage += err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage += err.response.data.error;
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += 'Bilinmeyen hata';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRowSelect = (entry) => {
    setSelectedRowId(entry.id);
    setSelectedEntry(entry);
    setIsModalOpen(true);
  };

  const handleCreateNew = () => {
    setSelectedEntry(null);
    setSelectedRowId(null);
    setIsModalOpen(true);
  };

  const handlePrintSelected = () => {
    if (selectedEntry) {
      setIsPrintModalOpen(true);
    }
  };

  const handlePrint = async () => {
    try {
      await reportManager.printStopSaleReport(selectedEntry);
      setIsPrintModalOpen(false);
    } catch (err) {
      setError('Rapor yazdırılırken hata oluştu: ' + err.message);
    }
  };

  const clearFilters = () => {
    setFilters(defaultFilters(hotels, maleOnly));
  };

  const filteredRows = getFilteredEntries();

  const roomTypesForHotel = roomTypes.filter((rt) => {
    if (!filters.hotel) return true;
    const hid = parseInt(filters.hotel, 10);
    const rtHotel = rt.hotel_id ?? rt.hotel;
    return !rtHotel || Number(rtHotel) === hid;
  });

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor('hotel_name', {
      header: 'Hotel',
      cell: (info) => <span className="text-gray-600 dark:text-gray-300">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('openstoptype_name', {
      header: 'Type',
      cell: (info) => <span className="text-gray-600 dark:text-gray-300">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('start_date', {
      header: 'Start',
      cell: (info) => <span className="text-gray-600 dark:text-gray-300">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('finish_date', {
      header: 'Finish',
      cell: (info) => <span className="text-gray-600 dark:text-gray-300">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('date_type_name', {
      header: 'Date Type',
      cell: (info) => <span className="text-gray-600 dark:text-gray-300">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('roomtype_name', {
      header: 'Room Type',
      cell: (info) => <span className="text-gray-600 dark:text-gray-300">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('created_by_name', {
      header: 'Created User',
      cell: (info) => <span className="text-gray-600 dark:text-gray-300">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('created_at', {
      header: 'Create Date',
      cell: (info) =>
        info.getValue() ? (
          <span className="text-gray-600 dark:text-gray-300">
            {new Date(info.getValue()).toLocaleString('tr-TR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ) : (
          <span className="text-gray-600 dark:text-gray-300">-</span>
        ),
    }),
  ];

  const fieldsetClass =
    'border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700';
  const legendClass = 'px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none';
  const inputClass =
    'w-full bg-transparent text-[11px] text-gray-900 dark:text-gray-100 outline-none dark:[color-scheme:dark]';

  return (
    <div className="space-y-6 p-4 md:p-6 md:px-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Open & Stop Sale</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Oda satış kısıtlarını listeleyin; satıra tıklayarak düzenleyin veya araç çubuğundan yeni kayıt oluşturun.
        </p>
      </div>

      {error && (
        <div className="px-0 md:px-0">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 flex items-start justify-between gap-2">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="shrink-0 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {loading && !error && (
        <div className="px-0 md:px-0">
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300">
            Veriler yükleniyor...
          </div>
        </div>
      )}

      <ActionToolbar>
        <IconActionButton icon={PlusIcon} onClick={handleCreateNew} title="Yeni Kayıt" />
        <IconActionButton
          icon={PrinterIcon}
          onClick={handlePrintSelected}
          disabled={!selectedEntry}
          title="Yazdır"
        />
      </ActionToolbar>

      <FilterPanel
        title="Filters"
        isOpen={filterPanelOpen}
        onToggle={() => setFilterPanelOpen((o) => !o)}
      >
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
          <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2 min-w-0">
            <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-2">Tarih ve lokasyon</p>
            <div className="grid grid-cols-2 gap-1.5">
              <fieldset className={fieldsetClass}>
                <legend className={legendClass}>Hotel</legend>
                <select
                  className={inputClass}
                  value={filters.hotel === '' ? '' : String(filters.hotel)}
                  onChange={(e) => handleFilterChange('hotel', e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">Seçiniz</option>
                  {hotels.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.hotel_name}
                    </option>
                  ))}
                </select>
              </fieldset>
              <fieldset className={fieldsetClass}>
                <legend className={legendClass}>Date Type</legend>
                <select
                  className={inputClass}
                  value={filters.dateType === '' ? '' : String(filters.dateType)}
                  onChange={(e) => handleFilterChange('dateType', e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">Seçiniz</option>
                  {dateTypes.map((dt) => (
                    <option key={dt.id} value={dt.id}>
                      {dt.date_type}
                    </option>
                  ))}
                </select>
              </fieldset>
              <fieldset className={fieldsetClass}>
                <legend className={legendClass}>Start Date</legend>
                <input
                  type="date"
                  className={inputClass}
                  value={filters.startDate || ''}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </fieldset>
              <fieldset className={fieldsetClass}>
                <legend className={legendClass}>Finish Date</legend>
                <input
                  type="date"
                  className={inputClass}
                  value={filters.finishDate || ''}
                  onChange={(e) => handleFilterChange('finishDate', e.target.value)}
                />
              </fieldset>
            </div>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2 min-w-0">
            <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-2">Pazar ve tip</p>
            <div className="grid grid-cols-2 gap-1.5">
              <fieldset className={fieldsetClass}>
                <legend className={legendClass}>Market</legend>
                <select
                  className={inputClass}
                  value={filters.market === '' ? '' : String(filters.market)}
                  onChange={(e) => handleFilterChange('market', e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">ALL</option>
                  {markets.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.market}
                    </option>
                  ))}
                </select>
              </fieldset>
              <fieldset className={fieldsetClass}>
                <legend className={legendClass}>Type</legend>
                <select
                  className={inputClass}
                  value={filters.type === '' ? '' : String(filters.type)}
                  onChange={(e) => handleFilterChange('type', e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">ALL</option>
                  {openStopTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.type}
                    </option>
                  ))}
                </select>
              </fieldset>
              <fieldset className={fieldsetClass}>
                <legend className={legendClass}>Rate Only</legend>
                <select
                  className={inputClass}
                  value={filters.rateOnly === '' ? '' : String(filters.rateOnly)}
                  onChange={(e) => handleFilterChange('rateOnly', e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">Seçiniz</option>
                  {maleOnly.map((mo) => (
                    <option key={mo.id} value={mo.id}>
                      {mo.is_maleonly}
                    </option>
                  ))}
                </select>
              </fieldset>
              <fieldset className={fieldsetClass}>
                <legend className={legendClass}>Room Type</legend>
                <select
                  className={inputClass}
                  value={filters.room === '' ? '' : String(filters.room)}
                  onChange={(e) => handleFilterChange('room', e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">ALL</option>
                  {roomTypesForHotel.map((rt) => (
                    <option key={rt.id} value={rt.id}>
                      {rt.roomtype_name}
                    </option>
                  ))}
                </select>
              </fieldset>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-700 pt-3 mt-3 flex justify-end">
          <FilterActions onClear={clearFilters} onSearch={loadStopSaleEntries} searchLabel="Ara" clearLabel="Temizle" />
        </div>
      </FilterPanel>

      <Card className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {filteredRows.length} / {stopSaleEntries.length} kayıt
            </span>
          </div>
          <DataTable
            data={filteredRows}
            columns={columns}
            isLoading={loading}
            onRowClick={handleRowSelect}
            selectedRowId={selectedRowId}
          />
        </div>
      </Card>

      <StopSaleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveStopSale}
        initialData={selectedEntry}
      />

      {isPrintModalOpen && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Print Open & Stop Sale</h3>
              <button
                type="button"
                onClick={() => setIsPrintModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4 space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <p>
                <strong className="text-gray-800 dark:text-gray-200">Hotel:</strong> {selectedEntry.hotel_name}
              </p>
              <p>
                <strong className="text-gray-800 dark:text-gray-200">Room Type:</strong> {selectedEntry.roomtype_name}
              </p>
              <p>
                <strong className="text-gray-800 dark:text-gray-200">Type:</strong> {selectedEntry.openstoptype_name}
              </p>
              <p>
                <strong className="text-gray-800 dark:text-gray-200">Period:</strong> {selectedEntry.start_date} -{' '}
                {selectedEntry.finish_date}
              </p>
              <p>
                <strong className="text-gray-800 dark:text-gray-200">Market:</strong> {selectedEntry.market_name}
              </p>
            </div>

            <div className="flex space-x-3">
              <Button onClick={handlePrint} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                <PrinterIcon className="h-4 w-4 mr-2 inline" />
                Print
              </Button>
              <Button onClick={() => setIsPrintModalOpen(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StopSaleDashboard;
